import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ReplayIcon from '@mui/icons-material/Replay'
import SendIcon from '@mui/icons-material/Send'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { FC, ReactNode, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/hooks/useAuth'
import { SX_DESTAQUE, idItemChecklist, useRolarAteDestaque } from '@/hooks/useRolarAteDestaque'
import { useReleituraEmVerificacao } from '@/hooks/useReleituraEmVerificacao'
import { documentoService } from '@/services/documentoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import {
  checklistReservaService,
  itensBloqueantesDoErro,
  mensagemErroChecklistReserva,
} from '@/services/checklistReservaService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { mensagemErroDocumento } from '@/utils/mensagemErroDocumento'
import {
  STATUS_JANELA_CONSULTOR,
  chaveDoGrupo,
  ehSolicitacaoCuradoria,
  gruposComItens,
  rotuloDoGrupo,
  rotuloDoItem,
  statusDoItem,
  temBasicoFaltando,
  temItemEmVerificacao,
} from '@/utils/checklistReserva'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { ehConsultor } from '@/components/layout/menu'
import {
  AlertaItensBloqueantes,
  CabecalhoVersaoChecklist,
  ChipStatusItem,
  DetalhesItemChecklist,
} from '@/components/documentos/ItemChecklistInfo'
import type { DocumentoItemChecklist, ItemBloqueanteChecklist } from '@/types/checklist'
import {
  ChecklistReserva,
  EstadoChecklist,
  ItemChecklistReserva,
  ReservaEstruturacao,
} from '@/types/estruturacao'

interface Props {
  reserva: ReservaEstruturacao
  checklist: ChecklistReserva | null
  /** Estado do GET /checklist no pai: carregando → spinner · erro → estado de erro com rebusca. */
  checklistEstado: EstadoChecklist
  /** Recarrega checklist + reserva no pai (após upload ou envio). */
  onRecarregar: () => void
  /** Rebusca SÓ o checklist ("Tentar novamente" do estado de erro e releitura "Em verificação"). */
  onRecarregarChecklist: () => void
  /**
   * Deep link da Central (L7 / Q40, `?item=`): chave do item (ou o rótulo, nos legados sem
   * chave — mesma identidade que o backend usa no destino). Destaca e rola até ele.
   */
  itemDestacado?: string | null
}

/** Identidade do item no deep link — a mesma do `CentralPendenciasService` (chave, senão rótulo). */
const idDoItem = (item: ItemChecklistReserva): string => idItemChecklist(item)

const ACEITOS = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.png'

/** Item cujo arquivo o consultor troca: recusado, vencido ou com falha de leitura. */
const pedeTroca = (item: ItemChecklistReserva): boolean => {
  const status = statusDoItem(item)
  return (
    status === 'RECUSADO' ||
    status === 'VENCIDO' ||
    (status === 'QUARENTENA' && (item.situacaoArquivos ?? []).includes('ERRO'))
  )
}

/**
 * Etapa 3 — Documentos (P3): checklist pela versão PRESA à reserva, agrupado pelas categorias
 * da versão, com o status de cada item vindo do backend (Faltando · Em verificação · Recebido ·
 * Validado · Recusado · Vencido), motivo da recusa, validade e "Reaproveitado". Itens pedidos
 * pela Curadoria (`sol-N`) vêm no grupo "Solicitados pela Curadoria" e o upload deles leva a
 * `exigenciaChave = sol-N`. Enquanto houver arquivo em verificação a etapa relê o checklist.
 *
 * O envio (RASCUNHO → ENVIADA) é decidido pelo backend: no 422
 * `DOCUMENTOS_OBRIGATORIOS_FALTANDO` a tela lista os itens que ele devolveu.
 */
export const EtapaDocumentos: FC<Props> = ({
  reserva,
  checklist,
  checklistEstado,
  onRecarregar,
  onRecarregarChecklist,
  itemDestacado = null,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [reaproveitando, setReaproveitando] = useState(false)
  const [bloqueio, setBloqueio] = useState<{ mensagem: string; itens: ItemBloqueanteChecklist[] } | null>(null)
  const [excluindo, setExcluindo] = useState<{ doc: DocumentoItemChecklist; item: ItemChecklistReserva } | null>(null)

  const consultor = ehConsultor(usuario?.permissoes)
  const naJanela = !reserva.status || STATUS_JANELA_CONSULTOR.includes(reserva.status)

  /**
   * Trocar/Excluir — mesma regra do checklist da reserva (`ChecklistDocumentosReserva`): o
   * back-office mexe em qualquer arquivo; o consultor só na janela da reserva (Rascunho ou Com
   * pendência) e nunca em item já validado. Que o arquivo seja dele quem confere é o backend
   * (409 com a mensagem própria) — o item do checklist não traz o autor do upload.
   */
  const podeAlterar = (item: ItemChecklistReserva) => !consultor || (naJanela && statusDoItem(item) !== 'VALIDADO')
  const grupos = useMemo(() => gruposComItens(checklist), [checklist])

  // Só os OBRIGATÓRIOS entram no aviso do que falta: um documento desejável que
  // aparecesse como pendência do envio não seria opcional (V45). `presente` é do backend.
  const pendentes = useMemo(
    () => grupos.flatMap((g) => g.itens).filter((i) => i.obrigatorio && !i.presente).map(rotuloDoItem),
    [grupos]
  )

  const alvoExiste = !!itemDestacado && grupos.some((g) => g.itens.some((i) => idDoItem(i) === itemDestacado))
  useRolarAteDestaque(alvoExiste ? `checklist-item-${itemDestacado}` : null, checklistEstado === 'ok')
  useReleituraEmVerificacao(checklistEstado === 'ok' && temItemEmVerificacao(checklist), onRecarregarChecklist)

  const handleUpload = async (item: ItemChecklistReserva, file: File) => {
    const rotulo = rotuloDoItem(item)
    setOcupado(idDoItem(item))
    try {
      await documentoService.upload(
        file, item.tipo ?? 'OUTRO', reserva.id, TIPO_ENTIDADE_OPORTUNIDADE,
        // Diz QUAL exigência este arquivo satisfaz (V45) — inclusive `sol-N` (solicitação da
        // Curadoria): upload sem a chave nunca satisfaz item de solicitação.
        item.chave
      )
      enqueueSnackbar(`${rotulo} enviado — em verificação`, { variant: 'success' })
      onRecarregar()
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message || `Falha ao enviar ${rotulo}`, { variant: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  const handleTrocar = async (item: ItemChecklistReserva, documentoId: string, file: File) => {
    const rotulo = rotuloDoItem(item)
    setOcupado(idDoItem(item))
    try {
      await documentoService.substituir(documentoId, file)
      enqueueSnackbar(`Arquivo de ${rotulo} trocado — em verificação`, { variant: 'success' })
      onRecarregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível trocar o arquivo de ${rotulo}`), { variant: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  const confirmarExclusao = async () => {
    if (!excluindo) return
    const { doc, item } = excluindo
    setOcupado(idDoItem(item))
    try {
      await documentoService.remover(doc.documentoId)
      enqueueSnackbar(`${doc.nome} excluído`, { variant: 'success' })
      onRecarregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível excluir ${doc.nome}`), { variant: 'error' })
    } finally {
      setExcluindo(null)
      setOcupado(null)
    }
  }

  const reaproveitarBasicos = async () => {
    setReaproveitando(true)
    try {
      const { solicitados } = await checklistReservaService.reaproveitarBasicos(reserva.id)
      enqueueSnackbar(
        solicitados > 0
          ? `${solicitados} documento(s) básico(s) em reaproveitamento — o checklist atualiza em instantes.`
          : 'Nenhum documento básico validado da sua carteira está disponível para reaproveitar.',
        { variant: solicitados > 0 ? 'success' : 'info' }
      )
      onRecarregarChecklist()
    } catch (e) {
      enqueueSnackbar(mensagemErroChecklistReserva(e, 'Não foi possível reaproveitar os documentos básicos.'), {
        variant: 'error',
      })
    } finally {
      setReaproveitando(false)
    }
  }

  const enviarReserva = async () => {
    if (!usuario?.id) return
    setEnviando(true)
    setBloqueio(null)
    try {
      await oportunidadeService.transicionarStatus(reserva.id, {
        novoStatus: 'ENVIADA',
        usuarioId: usuario.id,
      })
      enqueueSnackbar('Reserva enviada para análise da Originei', { variant: 'success' })
      onRecarregar()
    } catch (e) {
      const itens = itensBloqueantesDoErro(e)
      const mensagem = (e as { message?: string })?.message || 'Não foi possível enviar a reserva'
      if (itens.length > 0) {
        // 422 DOCUMENTOS_OBRIGATORIOS_FALTANDO: a mensagem e os itens como o backend mandou.
        setBloqueio({ mensagem, itens })
      } else {
        enqueueSnackbar(mensagem, { variant: 'error' })
      }
    } finally {
      setEnviando(false)
    }
  }

  if (checklistEstado === 'carregando') return <EstadoDeLista estado="carregando" />
  if (checklistEstado === 'erro' || !checklist) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar o checklist de documentos"
        acao={{ label: 'Tentar novamente', onClick: onRecarregarChecklist }}
      />
    )
  }

  const botaoArquivo = (
    rotuloBotao: string,
    ariaLabel: string,
    carregando: boolean,
    icone: ReactNode,
    onArquivo: (f: File) => void
  ) => (
    <Button
      component="label"
      size="small"
      variant="outlined"
      disabled={carregando}
      startIcon={carregando ? <CircularProgress size={14} color="inherit" /> : icone}
    >
      {rotuloBotao}
      <input
        type="file"
        hidden
        aria-label={ariaLabel}
        accept={ACEITOS}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onArquivo(f)
          e.target.value = ''
        }}
      />
    </Button>
  )

  return (
    <Box>
      <CabecalhoVersaoChecklist versao={checklist.versao} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {checklist.total > 0 && (
          <Typography variant="caption" color="text.secondary">
            {checklist.entregues ?? checklist.presentes} de {checklist.total} obrigatórios entregues
            {checklist.validados != null ? ` · ${checklist.validados} validados pela Originei` : ''}
          </Typography>
        )}
        {temBasicoFaltando(checklist) && (
          <Button
            size="small"
            onClick={() => void reaproveitarBasicos()}
            disabled={reaproveitando}
            startIcon={reaproveitando ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
          >
            Reaproveitar documentos básicos
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
        Formatos aceitos: PDF, Word, Excel, JPG ou PNG. Tamanho máximo de 25 MB por arquivo.
      </Typography>

      {checklist.total === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Não há documentos exigidos para o produto desta reserva.
        </Alert>
      )}

      {grupos.map((grupo, indice) => (
        <Box key={chaveDoGrupo(grupo, indice)} sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', mb: 1 }}
          >
            {rotuloDoGrupo(grupo)}
          </Typography>
          <List disablePadding>
            {grupo.itens.map((item) => {
              const status = statusDoItem(item)
              const rotulo = rotuloDoItem(item)
              const ocupadoItem = ocupado === idDoItem(item)
              const destacado = !!itemDestacado && idDoItem(item) === itemDestacado
              const ok = status === 'VALIDADO' || status === 'RECEBIDO'
              const docsAtivos = item.documentos ?? []
              return (
                <ListItem
                  key={idDoItem(item)}
                  id={`checklist-item-${idDoItem(item)}`}
                  role="group"
                  aria-label={`Item ${rotulo}`}
                  data-destacado={destacado ? 'true' : undefined}
                  sx={{
                    border: '1px solid',
                    borderColor: status === 'VALIDADO' ? 'success.main' : status === 'RECUSADO' || status === 'VENCIDO' ? 'error.main' : 'divider',
                    borderRadius: 1.5,
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1,
                    ...(destacado ? SX_DESTAQUE : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {ok ? (
                      <CheckCircleIcon color={status === 'VALIDADO' ? 'success' : 'info'} />
                    ) : status === 'RECUSADO' || status === 'VENCIDO' ? (
                      <ErrorOutlineIcon color="error" />
                    ) : (
                      <InsertDriveFileIcon color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    sx={{ flex: '1 1 240px' }}
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span>{rotulo}</span>
                        <ChipStatusItem status={status} />
                        {!item.obrigatorio && (
                          <Tooltip title="Desejável: não bloqueia o envio da reserva">
                            <Chip size="small" variant="outlined" label="Opcional" />
                          </Tooltip>
                        )}
                        {ehSolicitacaoCuradoria(item) && (
                          <Chip size="small" variant="outlined" color="secondary" label="Solicitado pela Curadoria" />
                        )}
                      </Stack>
                    }
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', component: 'div' }}
                    secondary={
                      <>
                        {status === 'FALTANDO' && 'Pendente — aguardando upload'}
                        <DetalhesItemChecklist item={item} />
                        {pedeTroca(item) && !podeAlterar(item) && (
                          <Typography variant="caption" component="div" color="text.secondary">
                            Para trocar este arquivo, a Originei vai abrir uma pendência nesta reserva.
                          </Typography>
                        )}
                      </>
                    }
                    secondaryTypographyProps={{ fontSize: '0.75rem', component: 'div' }}
                  />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {status === 'FALTANDO' &&
                      botaoArquivo('Enviar', `Enviar ${rotulo}`, ocupadoItem, <CloudUploadIcon />, (f) =>
                        void handleUpload(item, f)
                      )}
                    {podeAlterar(item) &&
                      docsAtivos.map((d) => (
                        <Stack key={d.documentoId} direction="row" spacing={0.5} component="span">
                          {botaoArquivo(
                            docsAtivos.length > 1 ? `Trocar ${d.nome}` : 'Trocar arquivo',
                            `Trocar ${d.nome}`,
                            ocupadoItem,
                            <SwapHorizIcon />,
                            (f) => void handleTrocar(item, d.documentoId, f)
                          )}
                          <Button
                            size="small"
                            color="error"
                            disabled={ocupadoItem}
                            startIcon={<DeleteOutlineIcon />}
                            aria-label={`Excluir ${d.nome}`}
                            onClick={() => setExcluindo({ doc: d, item })}
                          >
                            {docsAtivos.length > 1 ? `Excluir ${d.nome}` : 'Excluir arquivo'}
                          </Button>
                        </Stack>
                      ))}
                  </Stack>
                </ListItem>
              )
            })}
          </List>
        </Box>
      ))}

      {reserva.status === 'RASCUNHO' ? (
        <Box sx={{ mt: 3 }}>
          {pendentes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Para enviar, ainda faltam: {pendentes.join(', ')}.
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={() => void enviarReserva()}
            disabled={enviando}
            startIcon={enviando ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          >
            Enviar reserva
          </Button>
          {bloqueio && <AlertaItensBloqueantes mensagem={bloqueio.mensagem} itens={bloqueio.itens} />}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            O envio (Rascunho → Enviada) é validado pela Originei — documentos obrigatórios
            pendentes ou ainda em verificação bloqueiam o envio.
          </Typography>
        </Box>
      ) : (
        reserva.status && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Reserva já enviada — o checklist segue disponível para acompanhamento e
            complementação de documentos.
          </Typography>
        )
      )}

      <ConfirmDialog
        open={excluindo != null}
        title="Excluir documento"
        message={
          excluindo
            ? `Excluir "${excluindo.doc.nome}" de ${rotuloDoItem(excluindo.item)}? O item volta a ficar pendente se não houver outro arquivo.`
            : ''
        }
        confirmLabel="Excluir"
        isDangerous
        isLoading={excluindo != null && ocupado === idDoItem(excluindo.item)}
        onConfirm={() => void confirmarExclusao()}
        onCancel={() => setExcluindo(null)}
      />
    </Box>
  )
}

export default EtapaDocumentos
